if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mqlDesktop = window.matchMedia('(min-width: 990px)');

        // No thumbnail rail (stacked/columns layouts) - nothing for Slick to drive.
        if (!this.elements.thumbnails || typeof jQuery === 'undefined' || !jQuery.fn.slick) return;

        this.$viewerUl = jQuery(this.elements.viewer).find('ul[id^="Slider-Gallery"]').first();
        this.$thumbsUl = jQuery(this.elements.thumbnails).find('ul[id^="Slider-Thumbnails"]').first();
        if (!this.$viewerUl.length || !this.$thumbsUl.length) return;

        this.initSlick();

        if (this.mql.matches) this.removeListSemantic();

        this.bindThumbnailSelection(this.dataset.desktopLayout === 'thumbnail_left');
        this.bindDotSelection();

        // product-info.js diffs `media-gallery ul` direct-children-by-attribute
        // (querySelectorAll + insertBefore against the <ul> itself) whenever a
        // variant option changes, then calls setActiveMedia(id, true). Once
        // Slick is initialized it moves every <li> inside nested
        // .slick-list/.slick-track wrapper divs, which breaks that diffing
        // (insertBefore requires a direct child as the reference node). We
        // can't hook into the middle of that method, so we unslick as soon as
        // an option change is published - synchronously, before the fetch
        // that eventually triggers the diffing even starts - and rely on
        // setActiveMedia's prepend path (or the safety-net timer below, for
        // variants with no featured media at all) to reinitialize afterward.
        this.unsubscribeOptionChange = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ event }) => {
          if (!this.closest('product-info')?.contains(event.target)) return;
          this.destroySlick();
          clearTimeout(this.reinitSafetyTimer);
          this.reinitSafetyTimer = setTimeout(() => {
            if (!this.$viewerUl.hasClass('slick-initialized')) this.initSlick();
          }, 5000);
        });
      }

      disconnectedCallback() {
        this.unsubscribeOptionChange?.();
        clearTimeout(this.reinitSafetyTimer);
      }

      getArrowMarkup(templateSelector) {
        const template = this.querySelector(templateSelector);
        if (!template) return {};
        const prev = template.content.querySelector('.slider-button--prev');
        const next = template.content.querySelector('.slider-button--next');
        return {
          prevArrow: prev ? prev.outerHTML : undefined,
          nextArrow: next ? next.outerHTML : undefined,
        };
      }

      destroySlick() {
        if (this.$viewerUl.hasClass('slick-initialized')) this.$viewerUl.slick('unslick');
        if (this.$thumbsUl.hasClass('slick-initialized')) this.$thumbsUl.slick('unslick');
      }

      initSlick() {
        this.destroySlick();
        clearTimeout(this.reinitSafetyTimer);

        const isThumbLeft = this.dataset.desktopLayout === 'thumbnail_left';
        const viewerArrows = this.getArrowMarkup('template[id^="GalleryViewerArrows"]');
        const thumbArrows = this.getArrowMarkup('template[id^="GalleryThumbnailsArrows"]');
        const thumbCount = this.$thumbsUl.children('[data-target]').length;
        const isRtl = document.documentElement.dir === 'rtl';

        // No asNavFor: Slick's own nav-sync always repositions the linked
        // slider's track to make the target slide the first visible one
        // (there's no "already visible, leave it" case in its core), so
        // hovering/clicking an already-visible thumbnail would otherwise
        // yank the whole rail into a new scroll position. The viewer's own
        // afterChange (below) already drives the thumbnail highlight
        // manually, and bindThumbnailSelection drives the viewer directly -
        // so the thumbnail rail only ever moves via its own paging arrows.
        this.$viewerUl.slick({
          rtl: isRtl,
          infinite: true,
          slidesToShow: 1,
          slidesToScroll: 1,
          adaptiveHeight: true,
          draggable: true,
          arrows: Boolean(viewerArrows.prevArrow && viewerArrows.nextArrow),
          prevArrow: viewerArrows.prevArrow,
          nextArrow: viewerArrows.nextArrow,
        });

        const hasSlickThumbArrows = Boolean(thumbArrows.prevArrow && thumbArrows.nextArrow);
        let thumbRtl = isRtl;
        if (isThumbLeft && isRtl && window.innerWidth > 989) {
          // Slick has a core bug with vertical sliders in RTL mode (it offsets the Y axis by exactly 1 slide).
          // We disable RTL when it's a vertical slider, then dynamically toggle it back on mobile.
          thumbRtl = false;
        }

        const thumbOptions = {
          rtl: thumbRtl,
          infinite: false,
          slidesToScroll: 1,
          arrows: hasSlickThumbArrows,
          prevArrow: thumbArrows.prevArrow,
          nextArrow: thumbArrows.nextArrow,
        };

        let desktopThumbSlidesToShow = null;

        if (isThumbLeft) {
          // Desktop (default/base settings): vertical column showing 6
          // thumbnails at a time - the rest page in via the up/down arrows
          // (plus drag) once there are more than 6. --thumb-visible-count
          // drives the matching container height in section-main-
          // product.css, since that can't otherwise know this JS-computed
          // count.
          //
          // arrows: false here - desktop uses its own always-rendered
          // .thumbnail-slider__nav buttons (from the liquid template) instead
          // of Slick's own injected ones, wired up below via bindThumbNav().
          // Slick hides its injected arrows entirely (inline display: none,
          // not just disabled) whenever there's nothing to page, which
          // proved unreliable to override from the outside; owning the
          // buttons ourselves sidesteps that. Slick's responsive breakpoints
          // apply at/below the given width, so 989 and 749 here fall back to
          // the original Slick-injected horizontal thumbnail-strip
          // arrows (hasSlickThumbArrows) below desktop.
          desktopThumbSlidesToShow = Math.min(6, thumbCount);
          this.elements.thumbnails.style.setProperty('--thumb-visible-count', desktopThumbSlidesToShow);
          Object.assign(thumbOptions, {
            vertical: true,
            verticalSwiping: true,
            slidesToShow: desktopThumbSlidesToShow,
            arrows: false,
            responsive: [
              {
                breakpoint: 989,
                settings: {
                  vertical: false,
                  verticalSwiping: false,
                  arrows: hasSlickThumbArrows,
                  slidesToShow: Math.min(4, thumbCount),
                },
              },
              {
                breakpoint: 749,
                settings: {
                  vertical: false,
                  verticalSwiping: false,
                  arrows: hasSlickThumbArrows,
                  slidesToShow: Math.min(3, thumbCount),
                },
              },
            ],
          });
        } else {
          Object.assign(thumbOptions, {
            slidesToShow: Math.min(5, thumbCount),
            responsive: [
              { breakpoint: 989, settings: { slidesToShow: Math.min(4, thumbCount) } },
              { breakpoint: 749, settings: { slidesToShow: Math.min(3, thumbCount) } },
            ],
          });
        }

        this.$thumbsUl.slick(thumbOptions);

        if (isThumbLeft && isRtl) {
          let lastIsMobile = window.innerWidth <= 989;
          window.addEventListener('resize', () => {
            const isMobile = window.innerWidth <= 989;
            if (isMobile !== lastIsMobile) {
              lastIsMobile = isMobile;
              this.$thumbsUl.slick('slickSetOption', 'rtl', isMobile, true);
            }
          });
        }

        if (isThumbLeft) this.bindThumbNav(desktopThumbSlidesToShow);

        this.$viewerUl.off('afterChange.mediaGallery').on('afterChange.mediaGallery', (event, slick, currentSlide) => {
          this.syncActiveState(currentSlide);
        });

        // SSR already marks the correct slide is-active at index 0 (the
        // featured/first media is always rendered first), which is also
        // Slick's default initial slide - sync labels/counter to match.
        this.syncActiveState(this.$viewerUl.slick('slickCurrentSlide'));
      }

      // Wires the always-rendered .thumbnail-slider__nav buttons (desktop
      // vertical column only - see initSlick, which passes its own
      // slidesToShow so the "nothing left to page" check matches whatever
      // Slick is actually configured with) to page the thumbnail rail
      // directly, and keeps their disabled state in sync with the current
      // scroll position. Namespaced .off()/.on() so calling this again after
      // a later re-init doesn't stack duplicate handlers on the same static
      // buttons.
      bindThumbNav(slidesToShow) {
        const prevButton = this.elements.thumbnails.querySelector('[data-thumb-nav="prev"]');
        const nextButton = this.elements.thumbnails.querySelector('[data-thumb-nav="next"]');
        if (!prevButton || !nextButton) return;

        const $prev = jQuery(prevButton);
        const $next = jQuery(nextButton);

        const updateDisabled = () => {
          const currentSlide = this.$thumbsUl.slick('slickCurrentSlide');
          // .find(), not .children(): this runs after slick(thumbOptions)
          // has already moved every <li data-target> inside its own nested
          // .slick-list/.slick-track wrapper divs, so a direct-children-only
          // query here would always find zero, making slideCount 0 and the
          // next button permanently look disabled regardless of actual
          // thumbnail count.
          const slideCount = this.$thumbsUl.find('[data-target]').length;
          $prev.toggleClass('slick-disabled', currentSlide <= 0);
          $next.toggleClass('slick-disabled', currentSlide + slidesToShow >= slideCount);
        };

        $prev.off('click.thumbNav').on('click.thumbNav', () => this.$thumbsUl.slick('slickPrev'));
        $next.off('click.thumbNav').on('click.thumbNav', () => this.$thumbsUl.slick('slickNext'));
        this.$thumbsUl.off('afterChange.thumbNav').on('afterChange.thumbNav', updateDisabled);

        updateDisabled();
      }

      bindThumbnailSelection(isThumbLeft) {
        // Delegated handlers are arrow functions (need `this` bound to the
        // MediaGallery instance), so jQuery's usual "this = matched element"
        // delegation shortcut isn't available here - event.currentTarget
        // would resolve to the <ul> the listener is attached to, not the
        // hovered/clicked button. Walk up from the real event.target instead.
        // Always drives the viewer directly (never the thumbnail rail's own
        // slickGoTo) so selecting a thumbnail never repositions the rail.
        const goToThumbnail = (event) => {
          const button = event.target.closest('button');
          const li = button && button.closest('[data-target]');
          const index = li && li.getAttribute('data-slick-index');
          if (index === null || index === undefined) return;
          this.$viewerUl.slick('slickGoTo', Number(index));
        };

        this.$thumbsUl.on('click', 'button', goToThumbnail);

        if (isThumbLeft) {
          this.$thumbsUl.on('mouseenter', 'button', (event) => {
            if (!this.mqlDesktop.matches) return;
            goToThumbnail(event);
          });
        }
      }

      // Mobile-only dot pagination below the main viewer. Reuses the same
      // data-media-id -> data-slick-index lookup as setActiveMedia, so a dot
      // click always drives the viewer directly, same as a thumbnail click.
      bindDotSelection() {
        this.dotButtons = Array.from(this.querySelectorAll('[data-gallery-dot]'));
        this.dotButtons.forEach((button) => {
          button.addEventListener('click', () => this.setActiveMedia(button.dataset.mediaId));
        });
      }

      // setActiveMedia's prepend path (variant change) reorders the viewer's
      // <li> elements to match the new variant's media order, but the dots
      // live outside that <ul> so they're untouched by that reorder - keep
      // them in sync so dot order still matches slide order afterward.
      reorderDots() {
        if (!this.dotButtons?.length) return;
        const dotsContainer = this.dotButtons[0].parentElement;
        const order = this.$viewerUl
          .children('[data-media-id]')
          .map((_, el) => el.dataset.mediaId)
          .get();
        order.forEach((mediaId) => {
          const button = this.dotButtons.find((btn) => btn.dataset.mediaId === mediaId);
          if (button) dotsContainer.appendChild(button);
        });
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.$viewerUl) return;

        if (prepend) {
          // Unslick first: once initialized, Slick moves every <li> inside
          // nested wrapper divs, so `.children()` here would otherwise find
          // its arrow buttons/.slick-list instead of the actual slides.
          this.destroySlick();

          const $activeLi = this.$viewerUl.children(`[data-media-id="${mediaId}"]`);
          if ($activeLi.length && this.$viewerUl.children().first()[0] !== $activeLi[0]) {
            this.$viewerUl.prepend($activeLi);
          }
          const $activeThumb = this.$thumbsUl.children(`[data-target="${mediaId}"]`);
          if ($activeThumb.length && this.$thumbsUl.children().first()[0] !== $activeThumb[0]) {
            this.$thumbsUl.prepend($activeThumb);
          }
          this.initSlick();
          this.reorderDots();
          return;
        }

        // Slick assigns each slide a stable data-slick-index at init time,
        // regardless of how deeply it's nested inside .slick-list/.slick-track.
        const index = this.$viewerUl.find(`li[data-media-id="${mediaId}"]`).attr('data-slick-index');
        if (index === undefined) return;
        this.$viewerUl.slick('slickGoTo', Number(index));
      }

      syncActiveState(currentSlide) {
        const $viewerSlide = this.$viewerUl.find(`li[data-media-id][data-slick-index="${currentSlide}"]`);
        if (!$viewerSlide.length) return;

        this.$viewerUl.find('li[data-media-id]').removeClass('is-active');
        $viewerSlide.addClass('is-active');

        const $thumbSlide = this.$thumbsUl.find(`li[data-target][data-slick-index="${currentSlide}"]`);
        if ($thumbSlide.length) {
          this.$thumbsUl.find('button').removeAttr('aria-current');
          $thumbSlide.find('button').attr('aria-current', 'true');
        }

        const counterCurrent = this.elements.viewer.querySelector('.slider-counter--current');
        if (counterCurrent) counterCurrent.textContent = currentSlide + 1;

        const activeMediaId = $viewerSlide.attr('data-media-id');
        this.dotButtons?.forEach((button) => {
          button.classList.toggle('slider-counter__link--active', button.dataset.mediaId === activeMediaId);
        });

        this.preventStickyHeader();
        this.playActiveMedia($viewerSlide[0]);
        this.announceLiveRegion($viewerSlide[0], $thumbSlide.attr('data-media-position') || currentSlide + 1);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        this.$viewerUl.attr('role', 'presentation');
        this.$viewerUl.find('li').attr('role', 'presentation');
      }
    }
  );
}
